import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Navigation, 
  MapPin, 
  Clock, 
  Route,
  Car,
  Bike,
  Footprints,
  AlertTriangle,
  CheckCircle,
  Copy,
  ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';

interface DirectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  facility: {
    name: string;
    location: string;
    coordinates: [number, number];
    type: string;
  };
}

interface RouteOption {
  mode: 'driving' | 'walking' | 'cycling';
  duration: string;
  distance: string;
  traffic: 'light' | 'moderate' | 'heavy';
  icon: React.ComponentType<any>;
  description: string;
}

const DirectionsModal: React.FC<DirectionsModalProps> = ({ isOpen, onClose, facility }) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [selectedRoute, setSelectedRoute] = useState<RouteOption | null>(null);

  const routeOptions: RouteOption[] = [
    {
      mode: 'driving',
      duration: '8 min',
      distance: '2.3 miles',
      traffic: 'light',
      icon: Car,
      description: 'Fastest route via Main Street'
    },
    {
      mode: 'walking',
      duration: '24 min',
      distance: '1.8 miles',
      traffic: 'light',
      icon: Footprints,
      description: 'Direct pedestrian route'
    },
    {
      mode: 'cycling',
      duration: '12 min',
      distance: '2.1 miles',
      traffic: 'light',
      icon: Bike,
      description: 'Bike-friendly route with bike lanes'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      getCurrentLocation();
    }
  }, [isOpen]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsGettingLocation(false);
      },
      (error) => {
        setLocationError('Unable to get your location. Please enable location services.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const openInMaps = (route?: RouteOption) => {
    const destination = encodeURIComponent(facility.location);
    const coords = `${facility.coordinates[1]},${facility.coordinates[0]}`;
    
    // Detect device type
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    let url = '';
    
    if (isMobile) {
      if (isIOS) {
        // Apple Maps for iOS
        url = `maps://maps.apple.com/?daddr=${coords}&dirflg=${route?.mode === 'walking' ? 'w' : route?.mode === 'cycling' ? 'b' : 'd'}`;
      } else {
        // Google Maps for Android
        const mode = route?.mode === 'walking' ? 'walking' : route?.mode === 'cycling' ? 'bicycling' : 'driving';
        url = `google.navigation:q=${coords}&mode=${mode}`;
      }
    } else {
      // Web-based Google Maps for desktop
      const mode = route?.mode === 'walking' ? 'walking' : route?.mode === 'cycling' ? 'bicycling' : 'driving';
      url = `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=${mode}`;
    }
    
    // Try to open in native app first, fallback to web
    try {
      window.open(url, '_blank');
      toast.success('Opening navigation app...');
    } catch (error) {
      // Fallback to Google Maps web
      const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${coords}`;
      window.open(fallbackUrl, '_blank');
      toast.success('Opening Google Maps...');
    }
    
    onClose();
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(facility.location);
    toast.success('Address copied to clipboard');
  };

  const getTrafficColor = (traffic: string) => {
    switch (traffic) {
      case 'light': return 'text-accent-green';
      case 'moderate': return 'text-accent-orange';
      case 'heavy': return 'text-accent-red';
      default: return 'text-gray-400';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-400 rounded-lg flex items-center justify-center">
                  <Navigation className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Get Directions</h2>
                  <p className="text-sm text-gray-400">Navigate to {facility.name}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Destination Info */}
            <div className="p-6 border-b border-dark-700">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1">{facility.name}</h3>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{facility.location}</span>
                  </div>
                </div>
                <button
                  onClick={copyAddress}
                  className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                  title="Copy address"
                >
                  <Copy className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Location Status */}
            <div className="p-6 border-b border-dark-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Your Location</span>
                </div>
                {isGettingLocation ? (
                  <div className="flex items-center space-x-2 text-accent-orange">
                    <div className="w-3 h-3 border border-accent-orange border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">Getting location...</span>
                  </div>
                ) : userLocation ? (
                  <div className="flex items-center space-x-2 text-accent-green">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs">Location found</span>
                  </div>
                ) : (
                  <button
                    onClick={getCurrentLocation}
                    className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    Enable Location
                  </button>
                )}
              </div>
              {locationError && (
                <p className="text-xs text-accent-orange mt-2">{locationError}</p>
              )}
            </div>

            {/* Route Options */}
            <div className="p-6">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Route className="w-4 h-4" />
                Route Options
              </h4>
              
              <div className="space-y-3 mb-6">
                {routeOptions.map((route, index) => {
                  const Icon = route.icon;
                  return (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedRoute(route)}
                      className={`w-full p-4 rounded-lg border transition-all text-left ${
                        selectedRoute?.mode === route.mode
                          ? 'border-primary-400 bg-primary-400/10'
                          : 'border-dark-600 hover:border-dark-500 bg-dark-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            selectedRoute?.mode === route.mode ? 'bg-primary-400' : 'bg-dark-600'
                          }`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-white capitalize">{route.mode}</span>
                              <span className={`text-xs px-2 py-1 rounded ${getTrafficColor(route.traffic)} bg-current/10`}>
                                {route.traffic} traffic
                              </span>
                            </div>
                            <p className="text-sm text-gray-400">{route.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-white">{route.duration}</div>
                          <div className="text-sm text-gray-400">{route.distance}</div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openInMaps(selectedRoute || routeOptions[0])}
                  className="w-full bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Navigation className="w-5 h-5" />
                  Start Navigation
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openInMaps()}
                  className="w-full border border-dark-600 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-5 h-5" />
                  Open in Maps App
                </motion.button>
              </div>

              {/* Emergency Notice */}
              <div className="mt-4 p-3 bg-accent-orange/10 border border-accent-orange/30 rounded-lg">
                <div className="flex items-center space-x-2 text-accent-orange">
                  <AlertTriangle className="w-4 h-4" />
                  <p className="text-xs">
                    Routes may change due to emergency conditions. Follow official evacuation orders.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DirectionsModal;